import type { UntypedFormControl, AbstractControl, UntypedFormArray } from '@angular/forms';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Validators } from '@angular/forms';

import { CSS_RESTRICTIONS } from '@common/constants';
import { URL as URL_REGEX } from '@common/regexp';

import { ERadioGroupType } from '@ui/sp-radio-group';

import { WebBaseControlSettingsComponent } from '@web/widgets/common/abstract/web-base-control-settings.component';
import { EConditionType, EFontStyle, EMaskShapeType, EOverlayType } from '@common/enums';
import { DirectoryItemCategory, FmSourceType, type SelectedFileInterface } from '@sendpulse/file-manager';
import { extendWebFileDataPromise } from '@common/helpers';
import { type ReplacedFile } from '@components/sp-file-manager/src/types';
import { environment } from '@web-env/environment';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';

@Component({
    selector: 'web-image-settings',
    templateUrl: './image-settings.component.html',
    styleUrls: ['./image-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFileManagerComponentModule, WebColorPickerComponent],
})
export class ImageSettingsComponent extends WebBaseControlSettingsComponent<SelectedFileExtended> {
    public readonly FONT_SIZE_MAX = 72;
    public readonly EFontStyle = EFontStyle;
    public readonly EOverlayType = EOverlayType;
    public readonly EConditionType = EConditionType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EMaskShapeType = EMaskShapeType;
    public readonly FmSourceType = FmSourceType;
    public readonly DirectoryItemCategory = DirectoryItemCategory;

    public buttonDisabled: boolean = false;

    protected initForm(): void {
        this.form = this.fb.group({
            link: [null, [Validators.pattern(URL_REGEX)]],
            alt: null,
            isTargetBlank: [false],
            isImageEffect: null,
            condition: null,
            overlay: null,

            description: null,
            textSize: null,
            fontSize: null,
            textStyle: this.fb.array([]),
            textColor: null,
            textBackground: null,
            paddingTop: null,
            paddingBottom: null,
            paddingLeft: null,
            paddingRight: null,
            textPosition: null,
            textJustify: null,

            maskShape: null,

            overlayImage: null,
        });
    }

    protected setFormData(): void {
        const data: Partial<SelectedFileExtended> = {
            link: this.item.link,
            alt: this.item.alt,
            isTargetBlank: this.item.isTargetBlank,

            isImageEffect: this.item.isImageEffect,
            condition: this.item.condition,
            overlay: this.item.overlay,

            description: this.item.description,
            textSize: this.item.textSize,
            fontSize: this.item.fontSize,
            textStyle: this.item.textStyle,
            textColor: this.item.textColor,
            textBackground: this.item.textBackground,
            paddingTop: this.item.paddingTop,
            paddingBottom: this.item.paddingBottom,
            paddingLeft: this.item.paddingLeft,
            paddingRight: this.item.paddingRight,
            textPosition: this.item.textPosition,
            textJustify: this.item.textJustify,

            maskShape: this.item.maskShape,

            overlayImage: this.item.overlayImage,
        };

        this.textStyle.clear({ emitEvent: false });
        (data.textStyle || []).forEach((item: EFontStyle) =>
            this.textStyle.push(this.fb.control(item), { emitEvent: false })
        );
        this.form.setValue(data as any, { emitEvent: false });

        this.condition.valueChanges.subscribe((val) => {
            if (val === EConditionType.ON_HOVER && this.overlay.value === EOverlayType.MASK) {
                this.overlay.patchValue(null);
                this.maskShape.patchValue(null);
            }

            if (val === EConditionType.ALWAYS && this.overlay.value === EOverlayType.IMAGE) {
                this.overlayImage.patchValue(null);
            }
        });

        this.overlay.valueChanges.subscribe((val) => {
            if (val === EOverlayType.MASK) {
                this.maskShape.patchValue(EMaskShapeType.SHAPE_1);
            } else {
                this.maskShape.patchValue(null);
            }

            this.description.patchValue(null);
            this.overlayImage.patchValue(null);
        });

        this.link.valueChanges.subscribe((url) => {
            const validProtocolsRegex = /^(mailto:|ftp:|tel:|file:)/;

            if (!url.startsWith('http://') && !url.startsWith('https://') && !validProtocolsRegex.test(url) && url) {
                url = 'https://' + url;
                this.link.patchValue(url);
            }
        });
    }

    // ALT
    public get alt(): UntypedFormControl {
        return this.form.get('alt') as UntypedFormControl;
    }

    // LINK
    public get link(): UntypedFormControl {
        return this.form.get('link') as UntypedFormControl;
    }

    // TARGET
    public get isTargetBlank(): UntypedFormControl {
        return this.form.get('isTargetBlank') as UntypedFormControl;
    }

    // DESCRIPTION
    public get description(): UntypedFormControl {
        return this.form.get('description') as UntypedFormControl;
    }

    // TEXT_SIZE
    public get fontSize(): AbstractControl {
        return this.form.get('fontSize')!;
    }

    public setFontSize(value: string): void {
        this.fontSize.setValue(value);
    }

    // TEXT_POSITION
    public get textPosition(): AbstractControl {
        return this.form.get('textPosition')!;
    }

    public setTextPosition(value: string): void {
        this.textPosition.setValue(value);
    }

    // TEXT_ALIGN
    public get textJustify(): UntypedFormControl {
        return this.form.get('textJustify') as UntypedFormControl;
    }

    public setTextJustify(value: string): void {
        this.textJustify.setValue(value);
    }

    // TEXT_COLOR
    public get textColor(): AbstractControl {
        return this.form.get('textColor')!;
    }

    public setTextColor({ value }: ColorPickerEventPayload): void {
        this.textColor.setValue(value);
    }

    // TEXT_BACKGROUND
    public get textBackground(): AbstractControl {
        return this.form.get('textBackground')!;
    }

    public setTextBackground({ value }: ColorPickerEventPayload): void {
        this.textBackground.setValue(value);
    }

    public get MAX_DESCRIPTION_LENGTH(): number {
        return CSS_RESTRICTIONS.MAX_DESCRIPTION_LENGTH;
    }

    public get isImageEffect(): UntypedFormControl {
        return this.form.get('isImageEffect') as UntypedFormControl;
    }

    public get condition(): UntypedFormControl {
        return this.form.get('condition') as UntypedFormControl;
    }

    public get overlay(): UntypedFormControl {
        return this.form.get('overlay') as UntypedFormControl;
    }

    public get textStyle(): UntypedFormArray {
        return this.form.get('textStyle') as UntypedFormArray;
    }

    public get paddingTop(): UntypedFormControl {
        return this.form.get('paddingTop') as UntypedFormControl;
    }

    public get paddingBottom(): UntypedFormControl {
        return this.form.get('paddingBottom') as UntypedFormControl;
    }

    public get paddingLeft(): UntypedFormControl {
        return this.form.get('paddingLeft') as UntypedFormControl;
    }

    public get paddingRight(): UntypedFormControl {
        return this.form.get('paddingRight') as UntypedFormControl;
    }

    public get maskShape(): UntypedFormControl {
        return this.form.get('maskShape') as UntypedFormControl;
    }

    public get overlayImage(): UntypedFormControl {
        return this.form.get('overlayImage') as UntypedFormControl;
    }

    public get SIZE_MIN(): number {
        return CSS_RESTRICTIONS.SIZE_MIN;
    }

    public get SIZE_MAX(): number {
        return CSS_RESTRICTIONS.SIZE_MAX;
    }

    public setPaddingTop(value: number) {
        this.paddingTop.patchValue(value);
    }

    public setPaddingBottom(value: number) {
        this.paddingBottom.patchValue(value);
    }

    public setPaddingLeft(value: number) {
        this.paddingLeft.patchValue(value);
    }

    public setPaddingRight(value: number) {
        this.paddingRight.patchValue(value);
    }

    public setImageEffect(value: boolean) {
        this.isImageEffect.setValue(value);
    }

    public isActiveTextStyle(fontStyle: EFontStyle): boolean {
        return this.textStyle.value.includes(fontStyle);
    }

    public toggleActiveTextStyle(fontStyle: EFontStyle): void {
        const index = this.textStyle.value.findIndex((el: EFontStyle) => el === fontStyle);

        if (index > -1) {
            this.textStyle.removeAt(index);
        } else {
            this.textStyle.push(this.fb.control(fontStyle));
        }
    }

    public setAllPaddings(value: number): void {
        this.form.patchValue({
            paddingTop: value,
            paddingBottom: value,
            paddingLeft: value,
            paddingRight: value,
        });
    }

    public setMaskType(value: any): void {
        this.maskShape.patchValue(value);
        this.changeDetectorRef.detectChanges();
    }

    public async setImage(file: SelectedFileExtended | SelectedFileExtended[]): Promise<void> {
        if (Array.isArray(file)) return;

        this.buttonDisabled = true;
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.overlayImage.setValue(extendedFile);

        this.buttonDisabled = false;
    }

    public async replaceImage({ file }: ReplacedFile): Promise<void> {
        this.buttonDisabled = true;
        const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);
        this.overlayImage.setValue(extendedFile);

        this.buttonDisabled = false;
    }

    public removeFile(): void {
        this.overlayImage.setValue(null);
    }

    public toggleTargetBlank(value: boolean): void {
        this.isTargetBlank.setValue(value);
    }
}
